"use client";

import React from "react";
import { Layers, ShieldCheck, Grid3x3 as Grid3X3, Ruler, Scale, FileText } from "lucide-react";
import PageHero, { type PageHeroChip } from "@/components/PageHero";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type LocalizedText } from "@/lib/i18n";
export default function GuideHero() {
  const {
    lang
  } = useLanguage();
  const backLinkText: LocalizedText = {
    en: "Back to Selection & Design Guides",
    zh: "返回选型与设计指南",
    de: "Zurück zu Auswahl- & Designleitfäden",
    es: "Volver a guías de selección y diseño",
    fr: "Retour aux guides de choix et conception",
    pt: "Voltar para guias de seleção e projeto"
  };
  const eyebrowText: LocalizedText = {
    en: "Technical Specification Guide",
    zh: "技术规格选型指南",
    de: "Technischer Spezifikationsleitfaden",
    es: "Guía de especificaciones técnicas",
    fr: "Guide des spécifications techniques",
    pt: "Guia de especificações técnicas"
  };
  const titleText: LocalizedText = {
    en: "How to Choose Checkered Plate",
    zh: "如何选择花纹钢板",
    de: "Riffelblech richtig auswählen",
    es: "Cómo elegir chapa lagrimada",
    fr: "Comment choisir une tôle larmée",
    pt: "Como escolher chapa xadrez"
  };
  const descriptionText: LocalizedText = {
    en: "A practical guide to selecting pattern geometry, base thickness, material grade, and surface protection for walkways, stair treads, vehicle beds, running boards, and industrial floors. Available processes, specifications, and documentation are confirmed per project.",
    zh: "面向工业步道、楼梯踏步、车辆车厢踏板、设备操作平台及室内装饰地坪的系统选型指南。从工况受力、花纹形制、基板厚度到表面防护，助您匹配适用的花纹钢板与冲孔防滑板方案。具体加工工艺、规格参数及文件确认根据项目逐项确定。",
    de: "Ein praxisnaher Leitfaden zur Auswahl von Riffelgeometrie, Grundblechdicke, Werkstoffgüte und Oberflächenschutz für Laufstege, Treppenstufen, Fahrzeugladeflächen, Trittstufen und Industrieböden. Spezifikationen und Prüfzeugnisse werden projektbezogen bestätigt.",
    es: "Una guía práctica para seleccionar la geometría del relieve, el espesor base, el grado del material y la protección superficial para pasarelas, peldaños de escalera, plataformas de vehículos, estribos y suelos industriales. Especificaciones y documentación confirmadas por proyecto.",
    fr: "Un guide pratique pour sélectionner la géométrie du larmage, l'épaisseur de base, la nuance de matériau et la protection de surface pour passerelles, marches d'escalier, bennes de véhicules, marchepieds et sols industriels. Spécifications et documents confirmés par projet.",
    pt: "Um guia prático para selecionar a geometria do relevo, a espessura da base, a liga metálica e a proteção superficial para passarelas, degraus de escada, caçambas de veículos, estribos e pisos industriais. Especificações e documentos confirmados por projeto."
  };
  const primaryButtonText: LocalizedText = {
    en: "Request a quote",
    zh: "索取报价",
    de: "Angebot anfordern",
    es: "Solicitar cotización",
    fr: "Demander un devis",
    pt: "Solicitar cotação"
  };
  const secondaryButtonText: LocalizedText = {
    en: "Selection framework",
    zh: "选型流程指南",
    de: "Auswahlverfahren",
    es: "Marco de selección",
    fr: "Méthode de sélection",
    pt: "Guia de seleção"
  };
  const chips: PageHeroChip[] = [{
    icon: Ruler,
    text: {
      en: "Base thickness & span sizing",
      zh: "基板厚度与跨距核算",
      de: "Grundblechdicke & Stützweiten",
      es: "Espesor base y cálculo de vano",
      fr: "Épaisseur de base et calcul de portée",
      pt: "Espessura base e dimensionamento de vão"
    }
  }, {
    icon: Scale,
    text: {
      en: "Confirmed per project",
      zh: "按具体项目确认",
      de: "Je Projekt bestätigt",
      es: "Confirmado por proyecto",
      fr: "Confirmé par projet",
      pt: "Confirmado por projeto"
    }
  }, {
    icon: FileText,
    text: {
      en: "Documents on request",
      zh: "按需提供文件",
      de: "Dokumente auf Anfrage",
      es: "Documentos bajo solicitud",
      fr: "Documents sur demande",
      pt: "Documentos sob solicitação"
    }
  }];
  const guideHighlights = [{
    icon: Grid3X3,
    title: {
      en: "Pattern profiles",
      zh: "花纹形制选型",
      de: "Riffelprofile",
      es: "Perfiles de grabado",
      fr: "Profils de larmage",
      pt: "Perfis de relevo"
    },
    desc: {
      en: "Compare diamond, two-bar, five-bar raised ribs and fish-eye, crocodile-mouth punched plates.",
      zh: "对比菱形、两筋、五筋凸纹与鱼眼孔、鳄鱼嘴冲孔防滑板的特征与适用环境。",
      de: "Vergleichen Sie Rauten-, 2-Steg- und 5-Steg-Riffelungen mit gestanzten Rundnoppen- und Sicherheitsrost-Profilen.",
      es: "Compare nervaduras diamantadas, de dos barras, de cinco barras y chapas perforadas con embutición abombada o dentada.",
      fr: "Comparez les motifs losange, à 2 stries, à 5 stries et les tôles perforées à emboutis ronds ou dentelés.",
      pt: "Compare relevos diamante, duas barras, cinco barras e chapas perfuradas com furos repuxados ou perfis denteados."
    }
  }, {
    icon: Layers,
    title: {
      en: "Material & thickness",
      zh: "材质与基板厚度",
      de: "Werkstoff & Dicke",
      es: "Material y espesor",
      fr: "Matériau et épaisseur",
      pt: "Material e espessura"
    },
    desc: {
      en: "Assess carbon steel, stainless, or aluminum alloy base thickness under specific loads and spans.",
      zh: "结合腐蚀环境、结构自重与跨距荷载，核算碳钢、不锈钢或铝合金基板厚度。",
      de: "Bewerten Sie die Grundblechdicke für Kohlenstoffstahl, Edelstahl oder Aluminiumlegierungen unter definierten Lasten und Stützweiten.",
      es: "Evalúe el espesor de la chapa base de acero al carbono, inoxidable o aluminio según las cargas y vanos de apoyo.",
      fr: "Évaluez l'épaisseur de la tôle de base en acier au carbone, inox ou aluminium selon les charges et portées d'appui.",
      pt: "Avalie a espessura da chapa base de aço carbono, aço inoxidável ou alumínio sob cargas e vãos de apoio específicos."
    }
  }, {
    icon: ShieldCheck,
    title: {
      en: "Finishes & traction",
      zh: "表面防护与防滑",
      de: "Oberflächen & Rutschhemmung",
      es: "Acabados y tracción",
      fr: "Finitions et adhérence",
      pt: "Acabamentos e tração"
    },
    desc: {
      en: "Evaluate mill finish, painted, or hot-dip galvanized coating; slip resistance is confirmed per project.",
      zh: "评估原面、防锈漆、热浸镀锌与排水排屑需求，防滑性能根据项目具体要求确认。",
      de: "Walzblanke, lackierte oder feuerverzinkte Ausführungen abwägen; Rutschhemmung wird projektbezogen bestätigt.",
      es: "Evalúe acabado bruto, imprimado o galvanizado en caliente; la resistencia al deslizamiento se confirma según el proyecto.",
      fr: "Évaluez brut de laminage, peinture ou galvanisation à chaud ; la résistance au glissement est confirmée selon le projet.",
      pt: "Avalie acabamento bruto de laminação, pintado ou galvanizado a fogo; a resistência ao escorregamento é confirmada conforme o projeto."
    }
  }];
  return <PageHero id="checkeredplateguidehero" backLink={{
    label: backLinkText,
    to: "/SelectionDesignGuidesPage"
  }} eyebrow={eyebrowText} title={titleText} description={descriptionText} primaryButton={{
    label: primaryButtonText,
    to: "/ContactPage"
  }} secondaryButton={{
    label: secondaryButtonText,
    href: "#checkeredplateselectionguide"
  }} chips={chips}>
      {/* Quick Reference Highlight Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 pt-4">
        {guideHighlights.map((item, i) => {
        const IconComponent = item.icon;
        return <div key={item.title.en} data-index={i} className="bg-card border border-border rounded-xs p-5 shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-sm flex flex-col justify-between">
              <div>
                <div className="size-10 rounded-xs bg-muted flex items-center justify-center text-primary mb-3.5">
                  <IconComponent className="size-5" />
                </div>
                <h2 className="font-semibold text-base text-card-foreground font-serif mb-1.5">
                  {pick(item.title, lang)}
                </h2>
                <p className="text-sm text-muted-foreground leading-normal font-sans">
                  {pick(item.desc, lang)}
                </p>
              </div>
            </div>;
      })}
      </div>
    </PageHero>;
}